// client/src/dnd/emitter_tool/engine/OrbitManager.js


const DEG = Math.PI / 180;

/** Compute signed orbit speed in radians/sec from effect settings. */
function getOrbitSpeedRadS(es) {
  if (!es?.orbitEnabled) return 0;
  const dir = (es?.orbitDirection || "ccw") === "ccw" ? 1 : -1;
  const signed = Number.isFinite(es?.orbitSpeedDegPerS)
    ? Number(es.orbitSpeedDegPerS)
    : dir * Math.max(0, Number(es?.orbitSpeedAbsDps ?? 0));
  return (signed || 0) * DEG;
}

/** Convert metres → pixels using px-per-metre (ppm). */
function radiusPxFromMeters(m, ppm) {
  return Math.max(0, Number(m || 0)) * (ppm || 80);
}

/** Choose a starting phase angle (radians) for a sprite's orbit. */
function orbitPickStartPhase(es) {
  const mode = String(es?.orbitStartPhaseMode || "random").toLowerCase();
  if (mode === "aligned") return 0;
  const jitterDeg = Math.max(0, Math.min(360, Number(es?.orbitStartPhaseJitterDeg ?? 30)));
  const j = (Math.random() * 2 - 1) * jitterDeg * DEG;
  return Math.random() * Math.PI * 2 + j;
}

/** Decide if this particular sprite should get orbit applied (percent gating). */
function shouldApplyOrbit(es) {
  const mode = String(es?.orbitApplyMode || "all").toLowerCase();
  if (mode !== "percent") return true;
  const pct = Math.max(0, Math.min(100, Number(es?.orbitApplyPercent ?? 100)));
  return Math.random() * 100 < pct;
}

/** Safe global PIXI resolver without referencing undeclared or restricted globals. */
function resolveGlobalPIXI() {
  if (typeof window !== "undefined" && window && window.PIXI) return window.PIXI;
  if (typeof global !== "undefined" && global && global.PIXI) return global.PIXI;
  return null;
}

/**
 * Apply orbiting to all sprites in an emitter's container.
 *
 * @param {object} emitter - SimpleEmitter instance that exposes `.container` with PIXI Sprites.
 * @param {object} effectSettings - Parent/child effect settings that include orbit config.
 * @param {number} pxPerMeter - Scale for converting metres to pixels.
 * @param {number} dt - Delta time in seconds.
 * @param {object} [PIXIRef] - PIXI namespace; if omitted, tries window/global safely.
 */
export function applyOrbitToEmitter(emitter, effectSettings, pxPerMeter, dt, PIXIRef) {
  const es = effectSettings || {};
  if (!es?.orbitEnabled) return;

  const PIXI_ = PIXIRef || resolveGlobalPIXI();
  const cont = emitter?.container;
  if (!PIXI_ || !cont || !cont.children || cont.children.length === 0) return;

  const speedRadS = getOrbitSpeedRadS(es);
  const isEllipse = String(es?.orbitShape || "circle").toLowerCase() === "ellipse";
  const lock      = es?.orbitLockRadii !== false;

  // lazy init per-sprite orbit meta
  for (let i = 0; i < cont.children.length; i++) {
    const sp = cont.children[i];
    if (!sp || !sp.visible || !(sp instanceof PIXI_.Sprite)) continue;

    if (!sp._orbitInit && es.orbitEnabled) {
      sp._orbitInit = true;
      sp._orbit = null;

      if (shouldApplyOrbit(es)) {
        let rx, ry, tilt;
        if (isEllipse) {
          const rxM = Number(es?.orbitEllipseRxM ?? 1);
          const ryM = Number(es?.orbitEllipseRyM ?? 1);
          rx = radiusPxFromMeters(rxM, pxPerMeter);
          ry = radiusPxFromMeters(ryM, pxPerMeter);
          if (!(rx > 0 || ry > 0)) {
            const r = radiusPxFromMeters(1, pxPerMeter);
            rx = r; ry = r;
          }
          tilt = (Number(es?.orbitEllipseTiltDeg ?? 0)) * DEG;
        } else {
          const rMin = Number(es?.orbitRadiusMinM ?? 1);
          const rMax = Math.max(rMin, Number(es?.orbitRadiusMaxM ?? rMin));
          const rM   = lock ? rMin : (rMin + Math.random() * (rMax - rMin));
          rx = ry = radiusPxFromMeters(rM, pxPerMeter);
          tilt = 0;
        }
        sp._orbit = {
          angle: orbitPickStartPhase(es),
          rx, ry, tilt,
        };
      }
    }
  }

  // step (after SimpleEmitter.update placed sp.x/y at integrated centre)
  for (let i = 0; i < cont.children.length; i++) {
    const sp = cont.children[i];
    if (!sp || !sp.visible || !(sp instanceof PIXI_.Sprite)) continue;
    if (!sp._orbit) continue;

    if (speedRadS) sp._orbit.angle += speedRadS * dt;

    const baseX = sp.x;
    const baseY = sp.y;

    const a  = sp._orbit.angle || 0;
    const tC = Math.cos(sp._orbit.tilt || 0);
    const tS = Math.sin(sp._orbit.tilt || 0);
    const ox = Math.cos(a) * (sp._orbit.rx || 0);
    const oy = Math.sin(a) * (sp._orbit.ry || 0);

    sp.x = baseX + (tC * ox - tS * oy);
    sp.y = baseY + (tS * ox + tC * oy);
  }
}

export default {
  applyOrbitToEmitter,
};
